// // src/redux/donorSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";
import {
  saveToCacheDB,
  getFromCacheDB,
  clearCacheDB,
} from "../utils/indexedDBCache";

// Async actions for CRUD operations with filtering capabilities
export const fetchDonorById = createAsyncThunk(
  "donor/fetchDonorById",
  async (donorId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth?.token) {
        return rejectWithValue("Unauthorized: Missing token");
      }

      const response = await axiosInstance.get(`/api/donors/${donorId}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching donor:", error);
      return rejectWithValue(error.response?.data || "Failed to fetch donor");
    }
  },
);

export const fetchDonors = createAsyncThunk(
  "donor/fetchDonors",
  async (queryParams = {}, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const cacheKey = "all_donors";

      // Check if this is a silent refresh (has _silent flag)
      const isSilent = queryParams?._silent;
      const actualParams = { ...queryParams };
      delete actualParams._silent;

      // Try cache first (only if no query params and not silent)
      if (
        !isSilent &&
        (!actualParams || Object.keys(actualParams).length === 0)
      ) {
        const cachedData = await getFromCacheDB(cacheKey, 5 * 60 * 1000); // 5 min cache
        if (cachedData) {
          return { data: cachedData, silent: isSilent };
        }
      }

      const startTime = Date.now();

      const response = await axiosInstance.get("api/donors", {
        params: actualParams,
        headers: { Authorization: `Bearer ${auth.token}` },
        timeout: 60000,
      });

      const duration = Date.now() - startTime;

      // Cache only if no query params
      if (!actualParams || Object.keys(actualParams).length === 0) {
        await saveToCacheDB(cacheKey, response.data);
      }

      return { data: response.data, silent: isSilent };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch donors");
    }
  },
);

//Donor Id generated
export const lastDonorId = createAsyncThunk(
  "donor/lastDonorId",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axiosInstance.get("api/donors/last-id", {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      return response.data.lastDonorId; // ✅ get from object
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch last donor ID",
      );
    }
  },
);

//Import Excel file
export const importExcel = createAsyncThunk(
  "donors/importExcel",
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        "api/donors/import-excel",

        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      const data = response.data;
      if (!data.success) {
        return rejectWithValue({
          message: "Network issue:Import failed... Please try again",
          ...data,
        });
      }

      dispatch(fetchDonors());
      return data;
    } catch (error) {
      console.error("Error importing Excel:", error);
      console.error("Error response:", error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data || {
          message: "Network issue:Import failed... Please try again",
        },
      );
    }
  },
);

//import existing donor
export const importExistingDonors = createAsyncThunk(
  "donors/import-existing-excel",
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        "api/donors/import-existing-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      // Clear cache and silently refetch donors after successful upload
      await clearCacheDB("all_donors");
      dispatch(fetchDonors({ _silent: true }));

      return {
        insertedDonors: response.data?.insertedDonors || [],
        duplicateDonors: Array.isArray(response.data?.duplicateDonorsList)
          ? response.data.duplicateDonorsList
          : [],
        otherthancompleteStatusDonors: Array.isArray(
          response.data?.otherthancompleteStatusDonors,
        )
          ? response.data.otherthancompleteStatusDonors
          : [],
        duplicate: Array.isArray(response.data?.duplicate)
          ? response.data.duplicate
          : [],
        DonorsWithMissingId: Array.isArray(response.data?.DonorsWithMissingId)
          ? response.data.DonorsWithMissingId
          : [],
        DonorsWithMissingField: Array.isArray(
          response.data?.DonorsWithMissingField,
        )
          ? response.data.DonorsWithMissingField
          : [],
        insertedPayPurposes: Array.isArray(response.data?.insertedPayPurposes)
          ? response.data.insertedPayPurposes
          : [],
        insertedDonations: Array.isArray(response.data?.insertedDonations)
          ? response.data.insertedDonations
          : [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error importing donors");
    }
  },
);

//Reupload donations

export const reuploadDonations = createAsyncThunk(
  "donors/reupload-excel",
  async (file, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        "/api/donors/reupload-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

// Create a Donor
export const createDonor = createAsyncThunk(
  "donor/createDonor",
  async (donorData, { getState, rejectWithValue }) => {
    try {
      const { auth, donor } = getState();
      const donorId = Number(donor.lastDonorId) || 0;
      // const donorId = lastId + 1; // Increment donor ID in frontend
      const finalDonorData = {
        ...donorData,
        donorId,
      };

      const response = await axiosInstance.post("api/donors", finalDonorData, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error(
        "Error creating donor:",
        error.response?.data || error.message,
      );
      return rejectWithValue(error.response?.data || "Failed to create donor");
    }
  },
);

//Update a Donor
export const updateDonor = createAsyncThunk(
  "donor/updateDonor",
  async (donorData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const { _id } = donorData;

      const response = await axiosInstance.put(`api/donors/${_id}`, donorData, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating donor");
    }
  },
);

export const resetPrasad = createAsyncThunk(
  "prasad/resetPrasad",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("api/donors/reset-prasad");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Reset failed... Please try again",
      );
    }
  },
);

//Delete Donor
export const deleteDonor = createAsyncThunk(
  "donor/deleteDonor",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      await axiosInstance.delete(`api/donors/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete donor");
    }
  },
);

// Donor slice with additional fields
const donorSlice = createSlice({
  name: "donor",
  initialState: {
    donors: [],
    duplicateDonors: [],
    donations: [],
    data: [],
    insertedDonors: [],
    insertedDonations: [],
    selectedDonor: null,
    loading: false,
    error: null,
    importedPayPurposes: 0,
  },
  reducers: {
    clearImportError(state) {
      state.error = null;
    },
    clearImportData: (state) => {
      state.duplicateDonors = [];
      state.duplicate = [];
      state.incompleteStatusDonors = [];
      state.DonorsWithMissingId = [];
      state.DonorsWithMissingField = [];
      state.importedPayPurposes = 0;
    },
    clearMissingDonors(state) {
      state.missingDonors = [];
    },
    setSelectedDonor(state, action) {
      state.selectedDonor = action.payload;
    },
      updateDonorLocally: (state, action) => {
    const updatedDonor = action.payload;

    state.donors = state.donors.map((donor) =>
      donor._id === updatedDonor._id ? updatedDonor : donor
    );
  },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDonorById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonorById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDonor = action.payload;
      })
      .addCase(fetchDonorById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch donor";
      })
      .addCase(fetchDonors.pending, (state, action) => {
        // Only show loading if not a silent refresh
        if (!action.meta.arg?._silent) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchDonors.fulfilled, (state, action) => {
        // Only hide loading if not a silent refresh
        if (!action.payload.silent) {
          state.loading = false;
        }
        state.donors = Array.isArray(action.payload.data)
          ? action.payload.data
          : Array.isArray(action.payload)
            ? action.payload
            : [];
      })
      .addCase(fetchDonors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(lastDonorId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(lastDonorId.fulfilled, (state, action) => {
        state.loading = false;
        state.lastDonorId = !isNaN(action.payload) ? action.payload : 0;
      })
      .addCase(lastDonorId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(importExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.missingDonors = [];
      })
      .addCase(importExcel.fulfilled, (state, action) => {
        const { success, insertedDonors, insertedDonations, missingDonors } =
          action.payload;
        state.loading = false;
        state.error = null;
        if (success) {
          state.insertedDonors = insertedDonors;
          state.insertedDonations = insertedDonations;
          if (Array.isArray(action.payload.insertedDonors)) {
            state.donors.push(...action.payload.insertedDonors);
          } else {
            console.error(
              "Invalid payload format. Expected `insertedDonors` to be an array, got:",
              action.payload,
            );
          }
        }
        if (missingDonors?.length > 0) {
          state.missingDonors = missingDonors;
        }
        state.loading = false;
      })
      .addCase(importExcel.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.missingDonors?.length) {
          state.missingDonors = action.payload.missingDonors;
        }

        state.error = action.payload?.message;
      })

      .addCase(importExistingDonors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importExistingDonors.fulfilled, (state, action) => {
        state.loading = false;

        state.duplicateDonors = Array.isArray(action.payload.duplicateDonors)
          ? action.payload.duplicateDonors
          : [];
        state.incompleteStatusDonors = Array.isArray(
          action.payload.otherthancompleteStatusDonors,
        )
          ? action.payload.otherthancompleteStatusDonors
          : [];
        state.duplicate = Array.isArray(action.payload.duplicate)
          ? action.payload.duplicate
          : [];
        state.DonorsWithMissingId = Array.isArray(
          action.payload.DonorsWithMissingId,
        )
          ? action.payload.DonorsWithMissingId
          : [];
        state.DonorsWithMissingField = Array.isArray(
          action.payload.DonorsWithMissingField,
        )
          ? action.payload.DonorsWithMissingField
          : [];

        // Store imported PayPurpose data for instant update
        state.importedPayPurposes = action.payload.newPayPurposes || 0;
      })

      .addCase(importExistingDonors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(reuploadDonations.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(reuploadDonations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = Array.isArray(action.payload.data);
        // state.insertedCount = action.payload.insertedCount;
        state.successMessage = action.payload.message;
      })

      .addCase(reuploadDonations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || "Failed to upload donation Excel.";
      })

      .addCase(createDonor.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Clear cache when creating donor
        clearCacheDB("all_donors");
      })
      .addCase(createDonor.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns { message, donorId, donor }, extract the donor object
        const newDonor = action.payload.donor || action.payload;
        // Add new donor to the beginning so it appears at the top
        state.donors.unshift(newDonor);
        state.lastDonorId = newDonor.donorId;
      })
      .addCase(createDonor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create donor";
      })
      .addCase(updateDonor.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Clear cache when updating donor
        clearCacheDB("all_donors");
      })
      // .addCase(updateDonor.fulfilled, (state, action) => {
      //   state.loading = false;
      //   const { updatedDonor, updatedDonations } = action.payload;

      //   state.donors = state.donors.map((donor) =>
      //     donor._id === updatedDonor._id ? updatedDonor : donor
      //   );

      //   state.donations = state.donations.map(
      //     (donation) =>
      //       updatedDonations.find((upd) => upd._id === donation._id) || donation
      //   );
      // })
      .addCase(updateDonor.fulfilled, (state, action) => {
        state.loading = false;

        const updatedDonor = action.payload.updatedDonor || action.payload;

        // 1️⃣ Update donor list
        state.donors = state.donors.map((donor) =>
          donor._id === updatedDonor._id ? updatedDonor : donor,
        );

        // 2️⃣ Update donor fields inside donorSlice.donations IF exists
        if (Array.isArray(state.donations) && state.donations.length > 0) {
          const donorFields = (({ firstName, lastName, phone, email }) => ({
            firstName,
            lastName,
            phone,
            email,
          }))(updatedDonor);

          state.donations = state.donations.map((donation) =>
            donation.donorId === updatedDonor.donorId
              ? { ...donation, ...donorFields }
              : donation,
          );
        }
      })
      .addCase(updateDonor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resetPrasad.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.modifiedCount = 0;
      })
      .addCase(resetPrasad.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.modifiedCount = action.payload.modifiedCount;
      })
      .addCase(resetPrasad.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload || "Reset failed";
        state.modifiedCount = 0;
      })
      .addCase(deleteDonor.pending, (state) => {
        // Clear cache when deleting donor
        clearCacheDB("all_donors");
      })
      .addCase(deleteDonor.fulfilled, (state, action) => {
        state.donors = state.donors.filter(
          (donor) => donor._id !== action.payload,
        );
      });
  },
});

export const { clearImportError, clearMissingDonors, clearImportData, updateDonorLocally } =
  donorSlice.actions;

export default donorSlice.reducer;
